/* ══════════════════════════════════════════════════════════════
   AlumierMD checkout pop-up ("Before you check out")
   ──────────────────────────────────────────────────────────────
   Anyone can add AlumierMD to the basket. When the basket holds
   AlumierMD and the customer taps Checkout, this opens a pop-up
   with the one step they need (log in / create account / register
   with AlumierMD / move their account to us). Verified customers
   (patient_owned) see nothing.

   BASKET TRUTH IS SERVER-RENDERED: every checkout button carries
   data-skq-alumier="true|false" (+ data-skq-alumier-lines), output
   by Liquid in snippets/cart-drawer.liquid and sections/main-cart
   .liquid. The drawer / cart page re-render the button on every
   cart change, so the attribute is always current.

   NO ALUMIERMD IN THE BASKET = NO-OP. data-skq-alumier="false" →
   the handler returns before anything else: no preventDefault, no
   network call, no DOM change.

   Proceeding to checkout = set a one-shot bypass flag and .click()
   the same checkout button, so the drawer's discount/gift proxy,
   the subscription consent modal and the vanilla form submit all
   run exactly as they do today.

   Markup + CSS: snippets/alumier-checkout-gate.liquid.
   API contracts reused from assets/custom.js (not imported).
   ══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var root = document.getElementById('skq-alumier-gate');
  if (!root) return;

  /* ─── Config ─── */
  var HEROKU_URL = 'https://skintique-clinic-27e2f7a5cbf2.herokuapp.com';
  var CHECK_URL = HEROKU_URL + '/check_email.php';
  var REGISTER_URL = HEROKU_URL + '/';
  var PROXY_URL = 'https://alumier-proxy.skintique.co.uk';
  var API_TIMEOUT_MS = 10000;
  var CART_TIMEOUT_MS = 4000;               // fallback /cart.js lookup only
  var OWNED_TTL_MS = 24 * 60 * 60 * 1000;   // patient_owned is stable
  var PENDING_TTL_MS = 30 * 60 * 1000;      // login/create round trip
  var FAIL_OPEN_MS = 5 * 60 * 1000;         // let checkout through after an API failure
  var RESUME_SAFETY_MS = 12000;             // "Taking you to checkout…" screen never outlives this
  var RESUMING_CLASS = 'skq-ag-resuming';
  var LOAD_WAIT_MS = 5000;                  // resume waits for window 'load' at most this long   // set before first paint by layout/theme.liquid <head>
  var RESUME_PAGE_SAFETY_MS = 35000;        // fast resume page: leave for the origin if checkout hasn't started by then
  var CART_STATE_WAIT_MS = 5000;            // fast resume page: max wait for the drawer's checkout routing to know the basket
  var SHOW_TTL_MS = 2 * 60 * 1000;          // pop-up hand-over from the resume page to the origin page
  var VENDOR = 'AlumierMD';

  /* sessionStorage keys (our own — custom.js's alumier_verification_* cache is not trusted) */
  var K_OWNED = 'skq_ag_owned_';     // + email → timestamp
  var K_PENDING = 'skq_ag_pending';  // "<timestamp>|<origin path>", set when our login/create form is submitted
  var K_LAST_ORIGIN = 'skq_ag_last_origin'; // origin of the last resume (for Back onto an old /cart resume page)
  var RESUME_PARAM = 'alumier_gate';
  var K_CONSENT = 'skq_ag_consent';  // { consent, first, last, t }
  var K_DRAFT = 'skq_ag_draft_';     // + customerId → { sent: [variantIds], sig: 'lines already handled' }
  var K_SHOW = 'skq_ag_show';        // { s: 'register'|'transfer'|'basket', e: 0|1, t } — resume page → origin: open this there
  var K_SID = 'skq_ag_sid';          // anonymous random session id for the funnel counter (no personal data)
  var K_EVENTS = 'skq_ag_ev';        // { d: 'YYYY-MM-DD', k: { 'event:state[:method]': 1 } } — funnel events already sent today

  /* ─── Customer (from Liquid) ─── */
  var ds = root.dataset;
  var C = {
    loggedIn: ds.loggedIn === 'true',
    id: ds.customerId || '',
    email: (ds.customerEmail || '').trim(),
    first: ds.customerFirst || '',
    last: ds.customerLast || ''
  };
  var shopRoot = ds.root || '/';
  if (shopRoot.charAt(shopRoot.length - 1) !== '/') shopRoot += '/';
  /* True only on the fast resume page (layout/checkout-resume.liquid renders the pop-up with resume_here) */
  var onResumePage = ds.resumeHere === 'true';

  /* ─── State ─── */
  var S = {
    busy: false,            // gate flow running (only ever set for AlumierMD/unknown baskets)
    bypassClick: false,     // one-shot: next checkout click passes untouched
    passUntil: 0,           // fail-open / trusted-registration window (this page only)
    opener: null,           // checkout button that opened the pop-up
    lines: [],              // AlumierMD lines [{variantId, quantity}] for the draft backup
    current: null           // active state name
  };

  /* ═══════════════════════════════════════════
     Small helpers
     ═══════════════════════════════════════════ */

  function ssGet(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function ssSet(key, val) {
    try { sessionStorage.setItem(key, val); } catch (e) {}
  }
  function ssDel(key) {
    try { sessionStorage.removeItem(key); } catch (e) {}
  }
  function ssJSON(key) {
    try { return JSON.parse(ssGet(key) || 'null'); } catch (e) { return null; }
  }
  function warn(msg, err) {
    if (window.console && console.warn) console.warn('[SKQ AlumierGate] ' + msg, err || '');
  }

  function fetchWithTimeout(url, opts, ms) {
    opts = opts || {};
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = 0;
    if (ctrl) {
      opts.signal = ctrl.signal;
      timer = setTimeout(function () { ctrl.abort(); }, ms || API_TIMEOUT_MS);
    }
    return fetch(url, opts).then(function (r) {
      clearTimeout(timer);
      return r;
    }, function (err) {
      clearTimeout(timer);
      throw err;
    });
  }

  function postJSON(url, body) {
    return fetchWithTimeout(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }, API_TIMEOUT_MS);
  }

  /* Run fn after a promise settles either way (Promise.finally without relying on it) */
  function always(p, fn) {
    return p.then(function (v) { fn(); return v; }, function (err) { fn(); throw err; });
  }

  /* ─── Origin = the page where Checkout was tapped. Resume happens there, not /cart. ─── */

  /* Open-redirect guard: same-site relative path only ("/x", never "//x", "\\x" or a scheme) */
  var SAFE_PATH_RE = /^\/(?![\/\\])[^\s\\]*$/;
  function safePath(p) {
    return typeof p === 'string' && p.length < 2000 && SAFE_PATH_RE.test(p) ? p : '';
  }

  function withoutResumeParam(search) {
    var q = (search || '').replace(/^\?/, '').split('&').filter(function (kv) {
      return kv && kv.split('=')[0] !== RESUME_PARAM;
    }).join('&');
    return q ? '?' + q : '';
  }

  /* Current page as a resume origin; /cart, /account* and /checkout* fall back to home */
  function computeOrigin() {
    var path = window.location.pathname || '/';
    var bare = path.replace(/\/+$/, '') || '/';
    var cartPath = (ds.cartUrl || '/cart').replace(/\/+$/, '');
    if (bare === cartPath || /^\/(account|checkouts?)(\/|$)/.test(bare)) return shopRoot;
    return safePath(path + withoutResumeParam(window.location.search)) || shopRoot;
  }

  function resumeUrl(origin) {
    origin = safePath(origin) || shopRoot;
    return origin + (origin.indexOf('?') === -1 ? '?' : '&') + RESUME_PARAM + '=resume';
  }

  /* Where Shopify's log in / sign up returns to: the fast resume page when it's set up
     (data-resume-page, '' when missing / off), else the origin page as before. */
  function returnToUrl(origin) {
    var rp = safePath(ds.resumePage || '');
    return rp ? rp + '?' + RESUME_PARAM + '=resume' : resumeUrl(origin);
  }

  /* Origin stored in the pending flag ("<t>|<origin>") */
  function pendingOrigin() {
    var raw = ssGet(K_PENDING) || '';
    var bar = raw.indexOf('|');
    return bar > -1 ? safePath(raw.slice(bar + 1)) : '';
  }

  function isBackForward() {
    try {
      var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (nav && nav.type === 'back_forward') return true;
      return !!(performance.navigation && performance.navigation.type === 2);
    } catch (e) { return false; }
  }

  /* Owned cache: patient_owned only, 24h, keyed by email */
  function ownedKey() { return K_OWNED + C.email.toLowerCase(); }
  function isOwnedCached() {
    if (!C.loggedIn || !C.email) return false;
    var t = parseInt(ssGet(ownedKey()) || '0', 10);
    if (!t) return false;
    if (Date.now() - t > OWNED_TTL_MS) { ssDel(ownedKey()); return false; }
    return true;
  }
  function cacheOwned() { if (C.email) ssSet(ownedKey(), String(Date.now())); }

  /* True when this customer may check out without the pop-up */
  function customerCleared() {
    return isOwnedCached() || Date.now() < S.passUntil;
  }

  function isCheckoutButton(el) {
    return !!(el && el.getAttribute && el.getAttribute('name') === 'checkout'
      && (el.tagName === 'BUTTON' || el.tagName === 'INPUT'));
  }

  function inDrawer(btn) {
    return !!(btn && (btn._skqAgDrawer || (btn.closest && btn.closest('cart-drawer'))
      || (btn.getAttribute && btn.getAttribute('form') === 'cart-drawer-form')));
  }

  /* Re-find the checkout button if the drawer / cart summary re-rendered */
  function resolveButton(btn) {
    if (btn && btn.isConnected) return btn;
    if (inDrawer(btn)) {
      return document.querySelector('[name="checkout"][form="cart-drawer-form"]')
        || document.querySelector('cart-drawer [name="checkout"]');
    }
    return document.querySelector('#cart [name="checkout"]')
      || document.querySelector('[name="checkout"]');
  }

  /* 'true' | 'false' | null (attribute missing — unexpected) */
  function basketFlag(btn) {
    var v = btn && btn.getAttribute ? btn.getAttribute('data-skq-alumier') : null;
    return v === 'true' || v === 'false' ? v : null;
  }

  /* "11:2,33:1," → [{variantId: 11, quantity: 2}, …] (same variant on two lines is summed) */
  function parseLines(str) {
    var map = {};
    var order = [];
    (str || '').split(',').forEach(function (pair) {
      var bits = pair.split(':');
      var v = parseInt(bits[0], 10);
      var q = parseInt(bits[1], 10) || 1;
      if (!v) return;
      if (!map[v]) { map[v] = 0; order.push(v); }
      map[v] += q;
    });
    return order.map(function (v) { return { variantId: v, quantity: map[v] }; });
  }

  /* Leave the theme's own "agree to terms" alert to do its job */
  function termsUnchecked(btn) {
    var formId = btn.getAttribute('form') || (btn.form && btn.form.id);
    if (!formId) return false;
    var box = document.querySelector('.js-cart-terms-checkbox[form="' + formId + '"]');
    return !!(box && !box.checked);
  }

  function setButtonBusy(btn, on) {
    var b = resolveButton(btn);
    if (!b) return;
    if (on) { b.classList.add('skq-ag-busy'); b.setAttribute('aria-busy', 'true'); }
    else { b.classList.remove('skq-ag-busy'); b.removeAttribute('aria-busy'); }
  }

  /* ═══════════════════════════════════════════
     Fallback basket lookup — ONLY when a checkout button has no
     data-skq-alumier attribute (unexpected). Vendor only (no tags
     in /cart.js). Failure = proceed (fail open) with a warning.
     ═══════════════════════════════════════════ */

  function lookupBasket() {
    return fetchWithTimeout(shopRoot + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }, CART_TIMEOUT_MS)
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (cart) {
        var lines = [];
        ((cart && cart.items) || []).forEach(function (i) {
          if (i.vendor === VENDOR) lines.push({ variantId: i.variant_id, quantity: i.quantity || 1 });
        });
        return { has: lines.length > 0, lines: lines };
      })
      .catch(function (err) {
        warn('basket lookup failed, letting checkout through:', err);
        return { has: false, lines: [], failed: true };
      });
  }

  /* ═══════════════════════════════════════════
     AlumierMD API (contracts as in custom.js)
     ═══════════════════════════════════════════ */

  /* → 'patient_owned' | 'patient_not_owned' | 'patient_not_found' | 'error' */
  function checkStatus() {
    return postJSON(CHECK_URL, { email: C.email })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (data) {
        var code = data && data.verification_response && data.verification_response.reason_code;
        if (code === 'patient_owned' || code === 'patient_not_owned') return code;
        if (code) return 'patient_not_found';
        throw new Error('No reason_code');
      })
      .catch(function (err) {
        warn('check failed, failing open:', err);
        return 'error';
      });
  }

  /* → 'ok' | 'rejected' | 'error' */
  function registerPatient(first, last) {
    return postJSON(REGISTER_URL, { email: C.email, first_name: first, last_name: last })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res && res.error) { warn('register error:', res.error); return 'rejected'; }
        return 'ok';
      })
      .catch(function (err) {
        warn('register failed, failing open:', err);
        return 'error';
      });
  }

  /* Register, re-check, then route. Mirrors custom.js registerAlumierAndVerify:
     re-check not_owned → state 4; anything else after a good registration → trust it.
     @returns {Promise<'proceed'|'transfer'|'rejected'>} */
  function registerThenRecheck(first, last) {
    return registerPatient(first, last).then(function (res) {
      if (res === 'rejected') return 'rejected';
      if (res === 'error') { S.passUntil = Date.now() + FAIL_OPEN_MS; return 'proceed'; }
      return checkStatus().then(function (code) {
        if (code === 'patient_not_owned') return 'transfer';
        if (code === 'patient_owned') cacheOwned();
        S.passUntil = Date.now() + FAIL_OPEN_MS;
        return 'proceed';
      });
    });
  }

  /* State 4 backup: put the basket's AlumierMD lines on the customer's pending
     draft order. The proxy MERGES quantities, so only add variants that are not
     already on the draft, one request at a time. Runs once per distinct set of
     lines per session; a variant is only remembered as sent after a successful POST. */
  var _draftBusy = false;
  function saveDraftBackup() {
    if (_draftBusy || !C.id || !C.email) return;
    var lines = S.lines || [];
    if (!lines.length) return;
    var guardKey = K_DRAFT + C.id;
    var guard = ssJSON(guardKey) || {};
    var sent = guard.sent || [];
    var sig = lines.map(function (l) { return l.variantId; }).sort().join(',');
    if (guard.sig === sig) return;  // already handled these lines this session

    _draftBusy = true;
    var allOk = true;
    var p = fetchWithTimeout(PROXY_URL + '/api/draft-order/' + encodeURIComponent(C.id), { method: 'GET' }, API_TIMEOUT_MS)
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (draft) {
        var onDraft = {};
        if (draft && draft.exists && draft.draftOrder && draft.draftOrder.lineItems) {
          draft.draftOrder.lineItems.forEach(function (li) { onDraft[li.variantId] = true; });
        }
        var todo = lines.filter(function (l) {
          return !onDraft[l.variantId] && sent.indexOf(l.variantId) === -1;
        });
        return todo.reduce(function (chain, t) {
          return chain.then(function () {
            return postJSON(PROXY_URL + '/api/draft-order', {
              customerId: parseInt(C.id, 10),
              email: C.email,
              variantId: t.variantId,
              quantity: t.quantity
            })
              .then(function (r) { return r.json(); })
              .then(function (data) {
                if (data && data.success) sent.push(t.variantId);
                else allOk = false;
              })
              .catch(function () { allOk = false; });
          });
        }, Promise.resolve());
      })
      .then(function () {
        ssSet(guardKey, JSON.stringify({ sent: sent, sig: allOk ? sig : '' }));
      })
      .catch(function (err) {
        /* Proxy unreachable: skip rather than risk duplicate lines; retried next open */
        warn('draft backup skipped:', err);
      });
    always(p, function () { _draftBusy = false; });
  }

  /* POST /api/transfer-clicked — same body as the transfer page / custom.js
     ({ customerId: <int>, email, method? }). With a method this is send-method
     telemetry and starts reminder 1; without one it just stamps the click. */
  var _stamped = {};   // at most one POST per method per page view
  function stampTransferClick(method) {
    if (!C.id || !C.email) return;
    var key = method || 'show-me-how';
    if (_stamped[key]) return;
    _stamped[key] = true;
    var body = { customerId: parseInt(C.id, 10), email: C.email };
    if (method) body.method = method;
    try {
      fetch(PROXY_URL + '/api/transfer-clicked', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true, // survives the navigation / new tab
        body: JSON.stringify(body)
      }).catch(function () {});
    } catch (e) {}
  }

  /* ─── Funnel counter ───
     POST /api/gate-event { e, s, sid, m, j, c }. Fire-and-forget: never awaited by
     the gate, never blocks checkout, every failure swallowed.
       sid  random per-session id (sessionStorage; in-memory fallback).
       j    anonymous journey id. With analytics cookies allowed (Shopify customer
            privacy API) it is a random id kept in localStorage for a rolling 14
            days so a return visit joins the same journey; otherwise it is just sid
            and nothing is written to localStorage.
       c    logged-in shoppers only: a one-way SHA-256 of 'skq-gate:' + customer id,
            used server-side to match a later order to the journey. The raw id is
            never sent. Omitted if the hash is unavailable or not ready within 300ms.
     NO email, name, basket contents or other personal data is ever sent. Each
     event+state[+method] is sent at most once per sid per day.
     e: tap | shown | action | proceed
     s: tap → verified|login|register|transfer|create|error · shown/action → state ·
        proceed → verified|resume|failopen
     m: (action only) login|forgot|create|register|gmail|outlook-live|yahoo|mailto|transfer-page|why|close */
  var TRACK_URL = PROXY_URL + '/api/gate-event';
  var SID_RE = /^[A-Za-z0-9_-]{8,64}$/;
  var K_JID = 'skq_ag_jid';                  // localStorage { id, exp } — only with analytics consent
  var JID_TTL_MS = 14 * 24 * 60 * 60 * 1000; // rolling: refreshed each time it is used
  var C_HASH_WAIT_MS = 300;                  // hard cap on waiting for the customer hash
  var _sid = '';
  var _seen = null;
  var _cHash = null;     // null = not ready yet; '' = unavailable; else 64-char hex
  var _cHashP = null;

  function randomId() {
    var id = '';
    var chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    try {
      var buf = new Uint8Array(20);
      window.crypto.getRandomValues(buf);
      for (var i = 0; i < buf.length; i++) id += chars.charAt(buf[i] % chars.length);
    } catch (e) {
      id = '';
      for (var j = 0; j < 20; j++) id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  }

  function trackSid() {
    if (_sid) return _sid;
    var saved = ssGet(K_SID);
    if (saved && SID_RE.test(saved)) { _sid = saved; return _sid; }
    _sid = randomId();
    ssSet(K_SID, _sid);
    return _sid;
  }

  /* Analytics cookies allowed? Anything unclear counts as no. */
  function analyticsAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!cp && typeof cp.analyticsProcessingAllowed === 'function' && cp.analyticsProcessingAllowed() === true;
    } catch (e) { return false; }
  }

  /* Journey id: persistent 14 days with analytics consent, else the session id.
     Never writes localStorage without consent; drops a leftover id when consent is absent. */
  function journeyId() {
    var sid = trackSid();
    if (!analyticsAllowed()) {
      try { localStorage.removeItem(K_JID); } catch (e) {}
      return sid;
    }
    try {
      var now = Date.now();
      var id = '';
      var saved = JSON.parse(localStorage.getItem(K_JID) || 'null');
      if (saved && typeof saved.id === 'string' && SID_RE.test(saved.id) && typeof saved.exp === 'number' && saved.exp > now) id = saved.id;
      if (!id) id = randomId();
      localStorage.setItem(K_JID, JSON.stringify({ id: id, exp: now + JID_TTL_MS }));
      return id;
    } catch (e) { return sid; }
  }

  /* One-way tag for a logged-in shopper: sha256('skq-gate:' + customer id), lowercase hex.
     Computed once per page (started at load so it is ready long before a tap). */
  function customerTag() {
    if (_cHashP) return _cHashP;
    _cHashP = new Promise(function (resolve) {
      function done(v) { _cHash = v; resolve(v); }
      try {
        if (!C.id || !window.crypto || !window.crypto.subtle || typeof TextEncoder !== 'function') { done(''); return; }
        window.crypto.subtle.digest('SHA-256', new TextEncoder().encode('skq-gate:' + C.id)).then(function (buf) {
          var bytes = new Uint8Array(buf);
          var hex = '';
          for (var i = 0; i < bytes.length; i++) hex += (bytes[i] < 16 ? '0' : '') + bytes[i].toString(16);
          done(hex.length === 64 ? hex : '');
        }, function () { done(''); });
      } catch (e) { done(''); }
    });
    return _cHashP;
  }
  if (C.id) customerTag();

  /* True the first time a key is seen today in this session */
  function trackFirst(key) {
    var today = new Date().toISOString().slice(0, 10);
    if (!_seen || _seen.d !== today) {
      var saved = ssJSON(K_EVENTS);
      _seen = saved && saved.d === today && saved.k ? saved : { d: today, k: {} };
    }
    if (_seen.k[key]) return false;
    _seen.k[key] = 1;
    ssSet(K_EVENTS, JSON.stringify(_seen));
    return true;
  }

  function track(event, props) {
    try {
      var s = props && props.s;
      var m = props && props.m;
      if (!s) return;
      if (!trackFirst(event + ':' + s + (m ? ':' + m : ''))) return;
      var sid = trackSid();
      var body = { e: event, s: s, sid: sid, j: journeyId() };
      if (m) body.m = m;
      var send = function (c) {
        try {
          if (c) body.c = c;
          fetch(TRACK_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,   // survives the navigation to checkout / login
            body: JSON.stringify(body)
          }).catch(function () {});
        } catch (e) {}
      };
      if (!C.id || _cHash !== null) { send(_cHash || ''); return; }   // logged out, or hash already ready: send now
      /* Hash still computing: wait for it, but never more than C_HASH_WAIT_MS */
      var sent = false;
      var timer = setTimeout(function () { if (!sent) { sent = true; send(''); } }, C_HASH_WAIT_MS);
      customerTag().then(function (h) {
        if (sent) return;
        sent = true;
        clearTimeout(timer);
        send(h);
      });
    } catch (e) {}
  }

  /* A pop-up button press, in whichever state is open */
  function trackAction(method) {
    if (S.current) track('action', { s: S.current, m: method });
  }

  /* Verified customer with AlumierMD in the basket: no pop-up, straight through
     (the click handler returns before the gate runs, so count it here). */
  function trackVerifiedPass(btn) {
    if (basketFlag(btn) !== 'true' || !isOwnedCached()) return;
    track('tap', { s: 'verified' });
    track('proceed', { s: 'verified' });
  }

  /* ─── State 4: the ready-written transfer email ───
     Exactly what snippets/alumier-transfer-page.liquid sends: same To / Cc /
     Subject / body and the same compose-URL formats. Cc addresses are encoded
     one by one and joined with a LITERAL comma (an encoded %2C makes Outlook
     web drop the second address). */
  var TRANSFER_TO = 'customerservice@alumierlabs.com';
  var TRANSFER_CC = ['transfers@skintique.co.uk', 'transfers@mail.skintique.co.uk'];
  var TRANSFER_SUBJECT = 'Account Transfer Request';
  var TRANSFER_PAGE = '/pages/alumiermd-transfer';
  var MAILTO_FALLBACK_MS = 3000;

  /* Subject carries the customer's name so AlumierMD (and our transfers@
     copy) can see who it is from the inbox list. The proxy matches on
     "Account Transfer Request" anywhere in the subject, so this is safe. */
  function transferSubject() {
    var name = (C.first + ' ' + C.last).replace(/\s+/g, ' ').trim();
    return name && name.indexOf('@') === -1 ? TRANSFER_SUBJECT + ' - ' + name : TRANSFER_SUBJECT;
  }

  function transferBody() {
    var name = (C.first + ' ' + C.last).replace(/\s+/g, ' ').trim();
    if (!name || name.indexOf('@') !== -1) name = '[Your full name]';
    var email = C.email || '[Your Skintique account email]';
    return 'Dear AlumierMD Customer Services,\n\n' +
      'I would like to request that my AlumierMD account registration be transferred to Skintique.\n\n' +
      'Clinic: Skintique\n' +
      'Clinic reference: Y48Z4V9J\n\n' +
      'My details:\n' +
      '- Name: ' + name + '\n' +
      '- Email: ' + email + '\n\n' +
      'I would be grateful if you could process this at your earliest convenience.\n\n' +
      'Kind regards,\n' +
      name;
  }

  function transferLinks() {
    var enc = encodeURIComponent;
    var body = transferBody();
    var ccLiteral = TRANSFER_CC.map(enc).join(',');
    var qsBase = 'to=' + enc(TRANSFER_TO) + '&cc=' + ccLiteral +
      '&subject=' + enc(transferSubject()) + '&body=' + enc(body);
    return {
      'gmail': 'https://mail.google.com/mail/?view=cm&fs=1&to=' + enc(TRANSFER_TO) +
        '&cc=' + ccLiteral + '&su=' + enc(transferSubject()) + '&body=' + enc(body),
      'outlook-live': 'https://outlook.live.com/mail/deeplink/compose?path=/mail/action/compose&' + qsBase,
      'yahoo': 'https://compose.mail.yahoo.com/?' + qsBase,
      'mailto': 'mailto:' + TRANSFER_TO + '?cc=' + TRANSFER_CC.join(',') +
        '&subject=' + enc(transferSubject()) + '&body=' + enc(body)
    };
  }

  /* Fill in the four buttons; on iPhone/iPad lead with "Email app" (Apple Mail is
     almost always the default handler there — same rule as the transfer page). */
  /* Phone / tablet: iOS / Android, or a touch screen with a coarse pointer */
  function isHandheld() {
    var ua = navigator.userAgent || '';
    if (/iP(hone|ad|od)|Android/i.test(ua)) return true;
    var touch = ('ontouchstart' in window) || (navigator.maxTouchPoints || 0) > 0;
    var coarse = false;
    try { coarse = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches); } catch (e) {}
    return touch && coarse;
  }

  function prepareSendButtons() {
    var handheld = isHandheld();
    Array.prototype.forEach.call(root.querySelectorAll('[data-skq-ag-device]'), function (el) {
      el.hidden = el.getAttribute('data-skq-ag-device') !== (handheld ? 'phone' : 'desktop');
    });
    var didnt = root.querySelector('[data-skq-ag-mail-didnt]');
    if (didnt) didnt.hidden = true;
    var grid = root.querySelector('[data-skq-ag-mailgrid]');
    if (!grid) return;
    var links = transferLinks();
    Array.prototype.forEach.call(grid.querySelectorAll('[data-skq-ag-send]'), function (a) {
      var href = links[a.getAttribute('data-skq-ag-send')];
      if (href) a.setAttribute('href', href);
    });
    if (/iP(hone|ad|od)/.test(navigator.userAgent || '')) {
      var app = grid.querySelector('[data-skq-ag-send="mailto"]');
      if (app && grid.firstElementChild !== app) grid.insertBefore(app, grid.firstElementChild);
    }
  }

  /* "Email app" (mailto:) gives no feedback when nothing is set up to handle it.
     3s after the tap, if we're still here:
       phone / tablet → go to the step-by-step page, but only if this page is
                        still visible AND focused (no false positives);
       desktop        → never navigate; show an inline "Didn't open?" line.
     Cancelled by: the page hiding / unloading / losing focus, any other click or
     focus change in the pop-up, and the pop-up closing or changing state. */
  var _cancelMailtoFallback = function () {};
  function cancelMailtoFallback() { _cancelMailtoFallback(); }

  function startMailtoFallback(trigger) {
    cancelMailtoFallback();
    var handheld = isHandheld();
    var timer = 0;
    var onVis = function () { if (document.visibilityState === 'hidden') cancel(); };
    var onClick = function (e) { if (!trigger || !trigger.contains(e.target)) cancel(); };
    var onFocus = function (e) { if (!trigger || e.target !== trigger) cancel(); };
    var cancel = function () {
      clearTimeout(timer);
      timer = 0;
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', cancel);
      window.removeEventListener('blur', cancel);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('focusin', onFocus, true);
      _cancelMailtoFallback = function () {};
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', cancel);
    window.addEventListener('blur', cancel);
    /* capture, added after this click has finished dispatching */
    setTimeout(function () {
      if (!timer) return;
      document.addEventListener('click', onClick, true);
      document.addEventListener('focusin', onFocus, true);
    }, 0);
    _cancelMailtoFallback = cancel;
    timer = setTimeout(function () {
      cancel();
      if (handheld) {
        var focused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
        if (document.visibilityState === 'visible' && focused) {
          window.location.assign(TRANSFER_PAGE + '#alumier-send-yourself');
        }
      } else {
        var didnt = root.querySelector('[data-skq-ag-mail-didnt]');
        if (didnt && S.current === 'transfer' && !root.hidden) didnt.hidden = false;
      }
    }, MAILTO_FALLBACK_MS);
  }

  /* ═══════════════════════════════════════════
     Proceed to checkout (original path, untouched)
     ═══════════════════════════════════════════ */

  /* how = 'verified' | 'resume' | 'failopen' for the funnel counter; falsy = not counted */
  function proceed(btn, how) {
    S.busy = false;
    setButtonBusy(btn, false);
    close(true);
    var target = resolveButton(btn);
    if (!target) return;
    if (how) track('proceed', { s: how });
    S.bypassClick = true;
    target.click();          // dispatches synchronously; our click handler consumes the flag
    S.bypassClick = false;   // in case the button was disabled and no click fired
  }

  /* ═══════════════════════════════════════════
     Gate flow (AlumierMD basket, or unknown basket)
     ═══════════════════════════════════════════ */

  /* Where the basket answer comes from: the button's attribute, or (missing) /cart.js */
  function basketFor(btn) {
    var flag = basketFlag(btn);
    if (flag === 'true') {
      return Promise.resolve({ has: true, lines: parseLines(btn.getAttribute('data-skq-alumier-lines')) });
    }
    if (flag === 'false') return Promise.resolve({ has: false, lines: [] });
    return lookupBasket();
  }

  function gate(btn) {
    if (S.busy) return;
    S.busy = true;
    btn._skqAgDrawer = inDrawer(btn);
    var opened = null;   // state to open once busy is cleared
    var how = null;      // how we let them through (funnel counter); null = not counted
    var tapped = false;  // funnel 'tap' already sent for this click

    var p = basketFor(btn).then(function (r) {
      if (!r.has) return 'proceed';
      S.lines = r.lines;
      if (!C.loggedIn) { opened = 'login'; track('tap', { s: 'login' }); tapped = true; return; }
      if (customerCleared()) {
        if (isOwnedCached()) { how = 'verified'; track('tap', { s: 'verified' }); tapped = true; }
        return 'proceed';
      }

      setButtonBusy(btn, true);
      return checkStatus().then(function (code) {
        tapped = true;
        if (code === 'patient_owned') { cacheOwned(); how = 'verified'; track('tap', { s: 'verified' }); return 'proceed'; }
        if (code === 'patient_not_found') { opened = 'register'; track('tap', { s: 'register' }); return; }
        if (code === 'patient_not_owned') { opened = 'transfer'; track('tap', { s: 'transfer' }); return; }
        S.passUntil = Date.now() + FAIL_OPEN_MS;   // fail open
        how = 'failopen';
        track('tap', { s: 'error' });
        return 'proceed';
      });
    });

    always(p, function () {
      S.busy = false;
      setButtonBusy(btn, false);
    }).then(function (next) {
      if (next === 'proceed') proceed(btn, how);
      else if (opened) open(opened, btn);
    }, function (err) {
      warn('unexpected error, letting checkout through:', err);
      var counted = basketFlag(btn) === 'true';
      if (counted && !tapped) track('tap', { s: 'error' });
      proceed(btn, counted ? 'failopen' : null);
    });
  }

  /* Click: window capture runs before the drawer's document-capture listener */
  window.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[name="checkout"]') : null;
    if (!isCheckoutButton(btn)) return;

    if (S.bypassClick) { S.bypassClick = false; return; }
    var flag = basketFlag(btn);
    if (flag === 'false') return;                       // ← the no-AlumierMD no-op
    if (S.busy) { e.preventDefault(); e.stopImmediatePropagation(); return; }
    if (termsUnchecked(btn)) return;
    if (C.loggedIn && customerCleared()) { trackVerifiedPass(btn); return; }

    e.preventDefault();
    e.stopImmediatePropagation();
    gate(btn);
  }, true);

  /* Safety net: a checkout submit that didn't come through a click
     (Enter in a field, a script calling requestSubmit). */
  window.addEventListener('submit', function (e) {
    var sub = e.submitter;
    if (!isCheckoutButton(sub)) return;
    if (basketFlag(sub) === 'false') return;            // no-AlumierMD no-op
    if (S.busy) { e.preventDefault(); e.stopImmediatePropagation(); return; }
    if (C.loggedIn && customerCleared()) { if (!termsUnchecked(sub)) trackVerifiedPass(sub); return; }
    if (termsUnchecked(sub)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    gate(sub);
  }, true);

  window.addEventListener('pageshow', function (e) {
    S.busy = false;
    S.bypassClick = false;
    if (onResumePage) return;               // its <head> sends a restored resume page to the origin; keep the cover
    if (e.persisted) handleReturnVisit();   // back button from checkout: show the page (old /cart → origin)
  });

  /* ═══════════════════════════════════════════
     Pop-up
     ═══════════════════════════════════════════ */

  var panel = root.querySelector('.skq-ag__panel');
  var titleEl = document.getElementById('skq-ag-title');
  var defaultTitle = titleEl ? (titleEl.getAttribute('data-default-title') || titleEl.textContent) : '';
  var states = {};
  Array.prototype.forEach.call(root.querySelectorAll('[data-skq-ag-state]'), function (el) {
    states[el.getAttribute('data-skq-ag-state')] = el;
  });

  /* Login / create forms live in <template>; stamp them in on first use */
  function mountForm(name) {
    var slot = root.querySelector('[data-skq-ag-slot="' + name + '"]');
    if (!slot || slot.firstElementChild) return;
    var tpl = document.getElementById('skq-ag-tpl-' + name);
    if (!tpl || !tpl.content) return;
    slot.appendChild(document.importNode(tpl.content, true));
  }

  function focusables() {
    var scope = S.current && states[S.current];
    var list = [root.querySelector('.skq-ag__close'), root.querySelector('.skq-ag__info')];
    if (scope) {
      Array.prototype.push.apply(list, scope.querySelectorAll(
        'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])'
      ));
    }
    return list.filter(function (el) { return el && el.offsetParent !== null; });
  }

  function open(name, opener) {
    if (!states[name]) return;
    cancelMailtoFallback();   // switching state never navigates later
    if (opener) {
      S.opener = opener;
      root.classList.toggle('skq-ag--drawer', inDrawer(opener));
      root.classList.toggle('skq-ag--page', !inDrawer(opener));
    }
    if (name === 'login' || name === 'create') {
      mountForm(name);
      /* Come back to THIS page after Shopify's log in / sign up */
      S.origin = computeOrigin();
      Array.prototype.forEach.call(root.querySelectorAll('[data-skq-ag-slot] input[name="return_to"]'), function (inp) {
        inp.value = returnToUrl(S.origin);
      });
    }

    if (name === 'transfer') prepareSendButtons();

    for (var k in states) states[k].hidden = (k !== name);
    S.current = name;

    if (titleEl) titleEl.textContent = states[name].getAttribute('data-skq-ag-title') || defaultTitle;
    var msg = states[name].querySelector('.skq-ag__msg');
    if (msg && msg.id) root.setAttribute('aria-describedby', msg.id);
    else root.removeAttribute('aria-describedby');

    var err = states[name].querySelector('.skq-ag__error');
    if (err) { err.hidden = true; err.textContent = ''; }

    unhidePage();
    var wasHidden = root.hidden;
    root.hidden = false;
    if (wasHidden && !inDrawer(S.opener)) document.documentElement.classList.add('skq-ag-lock');

    var first = states[name].querySelector('input:not([type="hidden"]), a[href], button');
    if (first) first.focus({ preventScroll: true });

    if (name === 'transfer') saveDraftBackup();
    track('shown', { s: name });
  }

  function close(skipFocus) {
    cancelMailtoFallback();   // ×, Esc, backdrop: never navigate later
    if (root.hidden) return;
    root.hidden = true;
    S.current = null;
    document.documentElement.classList.remove('skq-ag-lock');
    if (!skipFocus) {
      var back = resolveButton(S.opener);
      if (back) back.focus({ preventScroll: true });
    }
  }

  /* Close: backdrop + × button */
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-skq-ag-close]')) { trackAction('close'); e.preventDefault(); close(); return; }
    if (e.target.closest('.skq-ag__info')) trackAction('why');            // link opens normally
    if (e.target.closest('.skq-ag__forgot a')) trackAction('forgot');     // link navigates normally
    var go = e.target.closest('[data-skq-ag-goto]');
    if (go) {
      if (go.getAttribute('data-skq-ag-goto') === 'create') trackAction('create');
      e.preventDefault(); open(go.getAttribute('data-skq-ag-goto')); return;
    }
    if (e.target.closest('[data-skq-ag-transfer]')) { trackAction('transfer-page'); stampTransferClick(); return; } // link then navigates normally
    var send = e.target.closest('[data-skq-ag-send]');
    if (send) {
      var method = send.getAttribute('data-skq-ag-send');
      trackAction(method);
      stampTransferClick(method);             // starts reminder 1; link opens normally
      if (method === 'mailto') startMailtoFallback(send);
    }
  });

  /* Esc closes on keyup (not keydown) so the same key-up can't reach the
     drawer's own Escape handler after focus returns to its button. */
  root.addEventListener('keyup', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') { trackAction('close'); e.stopPropagation(); close(); }
  });

  /* Keep Tab inside the pop-up */
  root.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var els = focusables();
    if (!els.length) return;
    var firstEl = els[0];
    var lastEl = els[els.length - 1];
    if (e.shiftKey && (document.activeElement === firstEl || !panel.contains(document.activeElement))) {
      e.preventDefault(); lastEl.focus();
    } else if (!e.shiftKey && document.activeElement === lastEl) {
      e.preventDefault(); firstEl.focus();
    }
  });

  function showError(stateName, msg) {
    var err = states[stateName] && states[stateName].querySelector('.skq-ag__error');
    if (!err) return;
    err.textContent = msg;
    err.hidden = false;
  }

  function setFormBusy(form, on) {
    var b = form.querySelector('button[type="submit"]');
    if (!b) return;
    b.disabled = on;
    b.classList.toggle('is-loading', on);
    if (on) b.setAttribute('aria-busy', 'true'); else b.removeAttribute('aria-busy');
  }

  /* State 3: register with AlumierMD (consent required) */
  var regForm = root.querySelector('[data-skq-ag-register]');
  if (regForm) {
    regForm.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!regForm.checkValidity()) { regForm.reportValidity(); return; }
      var first = regForm.querySelector('[name="first_name"]').value.trim();
      var last = regForm.querySelector('[name="last_name"]').value.trim();
      trackAction('register');
      setFormBusy(regForm, true);
      always(registerThenRecheck(first, last), function () { setFormBusy(regForm, false); })
        .then(function (outcome) {
          if (outcome === 'proceed') proceed(S.opener, isOwnedCached() ? 'verified' : 'failopen');
          else if (outcome === 'transfer') open('transfer');
          else showError('register', 'Something went wrong. Please try again.');
        }, function () {
          showError('register', 'Something went wrong. Please try again.');
        });
    });
  }

  /* States 1 + 5: real full-page Shopify forms. Remember where we were going. */
  root.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || !f.matches) return;
    if (f.matches('[data-skq-ag-create]')) {
      if (!f.checkValidity()) { e.preventDefault(); f.reportValidity(); return; }
      trackAction('create');
      ssSet(K_CONSENT, JSON.stringify({
        consent: true,
        first: (f.querySelector('[name="customer[first_name]"]') || {}).value || '',
        last: (f.querySelector('[name="customer[last_name]"]') || {}).value || '',
        t: Date.now()
      }));
      ssSet(K_PENDING, Date.now() + '|' + (S.origin || computeOrigin()));
    } else if (f.matches('[data-skq-ag-login]')) {
      if (!f.checkValidity()) { e.preventDefault(); f.reportValidity(); return; }
      trackAction('login');
      ssSet(K_PENDING, Date.now() + '|' + (S.origin || computeOrigin()));
    }
  });

  /* ═══════════════════════════════════════════
     Resume after login / account creation
     Only acts when THIS browser started a login/sign-up from the
     pop-up (fresh pending flag) AND the cart button says AlumierMD.
     ═══════════════════════════════════════════ */

  /* Clean the URL (so this history entry is just the origin page). Only an old
     /cart resume entry gets a marker in history.state (so Back onto it can be sent
     to the origin); everywhere else the existing state passes through untouched —
     facet-filters.js treats any non-null state as a filter entry. */
  function stripResumeParam(origin) {
    try {
      var path = window.location.pathname.replace(/\/+$/, '') || '/';
      var cartPath = (ds.cartUrl || '/cart').replace(/\/+$/, '');
      var state = history.state;
      if (path === cartPath) {
        var marked = {};
        if (state && typeof state === 'object') for (var k in state) marked[k] = state[k];
        marked.skqAgResumed = 1;
        if (origin) marked.skqAgOrigin = origin;
        state = marked;
      }
      history.replaceState(state, '', window.location.pathname + withoutResumeParam(window.location.search) + window.location.hash);
    } catch (e) {}
  }

  function pendingIsFresh() {
    var t = parseInt(ssGet(K_PENDING) || '0', 10);
    return !!t && Date.now() - t < PENDING_TTL_MS;
  }

  function takeConsent() {
    var c = ssJSON(K_CONSENT);
    ssDel(K_CONSENT);
    if (!c || !c.consent || !c.t || Date.now() - c.t > PENDING_TTL_MS) return null;
    return c;
  }

  /* "Taking you to checkout…" screen (class added in <head> before first paint) */
  var _holdTimer = 0;
  var _holdPoll = 0;
  function unhidePage() {
    clearTimeout(_holdTimer);
    clearInterval(_holdPoll);
    if (window.__skqAgUnhideTimer) clearTimeout(window.__skqAgUnhideTimer);
    var d = document.documentElement;
    if (d.classList.contains(RESUMING_CLASS)) d.classList.remove(RESUMING_CLASS);
  }

  /* Keep the screen up while checkout navigates (vanilla submit, or the drawer's
     gift/discount proxy → location.href). Reveal the page if something needs the
     customer instead (subscription consent modal), or after the safety timeout. */
  function holdUntilNavigation() {
    if (!document.documentElement.classList.contains(RESUMING_CLASS)) return;
    if (window.__skqAgUnhideTimer) clearTimeout(window.__skqAgUnhideTimer);   // restart the 12s from the click
    clearTimeout(_holdTimer);
    _holdTimer = setTimeout(unhidePage, RESUME_SAFETY_MS);
    clearInterval(_holdPoll);
    _holdPoll = setInterval(function () {
      if (document.querySelector('subscription-consent-modal[open]')) unhidePage();
    }, 250);
  }

  /* Run fn after window 'load', or after LOAD_WAIT_MS — whichever comes first */
  function whenLoaded(fn) {
    if (document.readyState === 'complete') { fn(); return; }
    var done = false;
    var cap = 0;
    var fire = function () {
      if (done) return;
      done = true;
      clearTimeout(cap);
      setTimeout(fn, 0);
    };
    cap = setTimeout(fire, LOAD_WAIT_MS);
    window.addEventListener('load', fire);
  }

  /* Resume page: old /cart links use the cart page button; every other page uses
     the drawer — open it (under the cover) and wait for its checkout button. */
  function acquireResumeButton(cb) {
    var path = window.location.pathname.replace(/\/+$/, '') || '/';
    var cartPath = (ds.cartUrl || '/cart').replace(/\/+$/, '');
    var pageBtn = path === cartPath ? document.querySelector('#cart [name="checkout"]') : null;
    if (pageBtn) { cb(pageBtn); return; }

    var drawer = document.querySelector('cart-drawer');
    if (!drawer) { cb(document.querySelector('[name="checkout"]')); return; }
    try {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: null } }));
    } catch (e) {
      if (typeof drawer.open === 'function') drawer.open();
    }
    var tries = 0;
    (function poll() {
      var b = document.querySelector('[name="checkout"][form="cart-drawer-form"]') || drawer.querySelector('[name="checkout"]');
      if (b || ++tries > 16) { cb(b); return; }   // ~2s, then give up (empty basket)
      setTimeout(poll, 125);
    })();
  }

  function resume() {
    var origin = pendingOrigin();
    stripResumeParam(origin);
    if (!pendingIsFresh()) { unhidePage(); return; }   // not ours (or expired): do nothing
    if (!C.loggedIn) { unhidePage(); return; }         // login didn't stick; keep the flag until it expires

    /* Validated: use up the flag now */
    ssDel(K_PENDING);
    if (origin) ssSet(K_LAST_ORIGIN, origin);
    var consent = takeConsent();

    /* Busy while we wait for the drawer / page load, so a Checkout tap meanwhile
       can't start a second flow. Every exit below clears it (proceed / always / here). */
    S.busy = true;
    acquireResumeButton(function (btn) {
      if (!btn || basketFlag(btn) !== 'true') {
        /* No AlumierMD (or no basket): never auto-checkout. */
        S.busy = false;
        unhidePage();
        return;
      }
      btn._skqAgDrawer = inDrawer(btn);
      S.lines = parseLines(btn.getAttribute('data-skq-alumier-lines'));
      /* Run once window 'load' has fired, so async app embeds (BOGOS,
         RecurPay…) have attached their handlers. Cover stays up meanwhile. */
      whenLoaded(function () {
        try { runResume(btn, consent); }
        catch (err) { S.busy = false; warn('resume failed:', err); unhidePage(); }
      });
    });
  }

  /* After log in / sign up: status check, plus registration with the consent given in the
     create-account form when needed. Shared by the origin-page resume and the fast resume page.
     @returns {Promise<{next: 'proceed'} | {opened: 'register'|'transfer', flagReg: boolean}>} */
  function resumeDecision(consent) {
    return checkStatus().then(function (code) {
      if (code === 'patient_owned') { cacheOwned(); return { next: 'proceed' }; }
      if (code === 'patient_not_owned') return { opened: 'transfer' };
      if (code === 'error') { S.passUntil = Date.now() + FAIL_OPEN_MS; return { next: 'proceed' }; }
      /* patient_not_found */
      if (!consent) return { opened: 'register' };
      return registerThenRecheck(consent.first || C.first, consent.last || C.last).then(function (outcome) {
        if (outcome === 'proceed') return { next: 'proceed' };
        return { opened: outcome === 'transfer' ? 'transfer' : 'register', flagReg: outcome === 'rejected' };
      });
    });
  }

  function runResume(btn, consent) {
    var go = function () {
      if (termsUnchecked(btn)) unhidePage();   // the terms alert needs the page
      else holdUntilNavigation();
      proceed(btn, 'resume');
    };
    if (customerCleared()) { go(); return; }

    S.busy = true;
    setButtonBusy(btn, true);

    var p = resumeDecision(consent);

    always(p, function () {
      S.busy = false;
      setButtonBusy(btn, false);
    }).then(function (d) {
      if (d.next === 'proceed') { go(); return; }
      unhidePage();
      if (d.opened) open(d.opened, btn);
      if (d.flagReg) showError('register', 'Something went wrong. Please try again.');
    }, function (err) {
      warn('resume failed:', err);
      unhidePage();
    });
  }

  /* ═══════════════════════════════════════════
     Fast resume page (layout/checkout-resume.liquid)
     A light page (no header / footer / grid) with the cart drawer
     rendered hidden. The status check starts at script start, in
     parallel with the drawer's checkout routing getting ready, then
     the drawer's real Checkout button is clicked as on any page.
     Anything that needs the customer goes back to the ORIGIN page:
     pop-ups and the basket open there. Never stranded here.
     ═══════════════════════════════════════════ */

  function sameBarePath(a, b) {
    var strip = function (p) { return (p || '').split('?')[0].split('#')[0].replace(/\/+$/, '') || '/'; };
    return strip(a) === strip(b);
  }

  /* Replace this history entry with the origin (Back never returns to the resume page).
     state 'register' | 'transfer' = open that pop-up there; 'basket' = open the basket there. */
  function leaveResumePage(origin, state, flagReg) {
    clearTimeout(_resumeSafety);
    clearInterval(_resumePoll);
    clearTimeout(window.__skqAgResumeSafety);
    var dest = safePath(origin) || shopRoot;
    if (sameBarePath(dest, window.location.pathname)) dest = shopRoot;
    if (state) {
      ssSet(K_SHOW, JSON.stringify({ s: state, e: flagReg ? 1 : 0, t: Date.now() }));
      dest += (dest.indexOf('?') === -1 ? '?' : '&') + RESUME_PARAM + '=show';
    }
    try { window.location.replace(dest); } catch (e) { window.location.href = dest; }
  }

  /* Safety net: if checkout hasn't taken over by now, go to the origin with the basket open.
     Paused while the subscription consent modal is up (the customer is reading it). */
  var _resumeSafety = 0;
  var _resumePoll = 0;
  function armResumeSafety(origin) {
    clearTimeout(window.__skqAgResumeSafety);   // the <head> one is replaced by this
    clearTimeout(_resumeSafety);
    _resumeSafety = setTimeout(function () {
      if (document.querySelector('subscription-consent-modal[open]')) return;
      warn('resume page timed out, going back to the shop page');
      leaveResumePage(origin, 'basket');
    }, RESUME_PAGE_SAFETY_MS);
  }

  function domReady() {
    return new Promise(function (resolve) {
      if (document.readyState !== 'loading') { resolve(); return; }
      document.addEventListener('DOMContentLoaded', function () { resolve(); });
    });
  }

  /* The drawer's checkout routing is ready to treat a click exactly like a customer's tap:
     snippets/cart-drawer-exclusivity.liquid has read the basket (sub / discount / gift →
     proxy or native checkout) and the subscription consent modal is upgraded. Capped:
     past the cap we carry on, as the origin-page resume would. */
  function checkoutWired() {
    var waits = [domReady().then(function () {
      return new Promise(function (resolve) {
        var start = Date.now();
        (function poll() {
          if (window.__skqCartExReady) { resolve(); return; }
          if (Date.now() - start > CART_STATE_WAIT_MS) { warn('basket state not ready, carrying on'); resolve(); return; }
          setTimeout(poll, 50);
        })();
      });
    })];
    if (document.querySelector('subscription-consent-modal') && window.customElements && customElements.whenDefined) {
      waits.push(Promise.race([
        customElements.whenDefined('subscription-consent-modal'),
        new Promise(function (resolve) { setTimeout(resolve, CART_STATE_WAIT_MS); })
      ]));
    }
    return Promise.all(waits);
  }

  /* After the Checkout click: keep "Taking you to checkout…" up until the browser leaves.
     Subscription baskets: show the page while the consent modal is open; accepted → cover
     again (the proxy is creating the checkout); declined → origin with the basket open. */
  function holdOnResumePage(origin) {
    armResumeSafety(origin);
    var shown = false;
    var declined = false;
    var noteDecline = function (e) {
      if (!shown) return;
      if (e.type === 'click') {
        if (e.target && e.target.closest && e.target.closest('.js-consent-decline')) declined = true;
      } else if (e.key === 'Escape' || e.key === 'Esc') {
        declined = true;   // Escape = decline in the consent modal
      }
    };
    document.addEventListener('click', noteDecline, true);
    document.addEventListener('keyup', noteDecline, true);
    clearInterval(_resumePoll);
    _resumePoll = setInterval(function () {
      var modalOpen = !!document.querySelector('subscription-consent-modal[open]');
      if (modalOpen && !shown) {
        shown = true;
        declined = false;
        clearTimeout(_resumeSafety);
        unhidePage();
      } else if (!modalOpen && shown) {
        shown = false;
        if (declined) { leaveResumePage(origin, 'basket'); return; }
        document.documentElement.classList.add(RESUMING_CLASS);
        armResumeSafety(origin);
      }
    }, 250);
  }

  function resumeOnResumePage() {
    var origin = pendingOrigin() || safePath(ssGet(K_LAST_ORIGIN)) || shopRoot;
    if (!pendingIsFresh() || !C.loggedIn) { leaveResumePage(origin); return; }   // not ours / login didn't stick

    /* Validated: use up the flag now (Back from checkout then goes to the origin) */
    ssDel(K_PENDING);
    ssSet(K_LAST_ORIGIN, origin);
    var consent = takeConsent();

    /* Basket truth is server-rendered on the drawer's button, already parsed (deferred script) */
    var btn = document.querySelector('[name="checkout"][form="cart-drawer-form"]')
      || document.querySelector('cart-drawer [name="checkout"]');
    if (!btn || btn.disabled || basketFlag(btn) !== 'true') { leaveResumePage(origin); return; }   // no AlumierMD / empty: never auto-checkout
    btn._skqAgDrawer = true;
    S.lines = parseLines(btn.getAttribute('data-skq-alumier-lines'));
    S.busy = true;
    armResumeSafety(origin);

    /* Both start now and run in parallel */
    var decision = customerCleared() ? Promise.resolve({ next: 'proceed' }) : resumeDecision(consent);
    Promise.all([decision, checkoutWired()]).then(function (r) {
      S.busy = false;
      var d = r[0] || {};
      if (d.next !== 'proceed') { leaveResumePage(origin, d.opened || 'basket', d.flagReg); return; }
      var b = resolveButton(btn);
      if (!b || b.disabled || basketFlag(b) !== 'true' || termsUnchecked(b)) {
        leaveResumePage(origin, 'basket');   // basket changed meanwhile / terms box: the customer takes it from there
        return;
      }
      holdOnResumePage(origin);
      proceed(b, 'resume');
    }).catch(function (err) {
      S.busy = false;
      warn('resume page failed:', err);
      leaveResumePage(origin, 'basket');
    });
  }

  /* Origin page, arriving from the resume page with ?alumier_gate=show: open the pop-up
     state (or just the basket) it handed over. One-shot, short-lived, sessionStorage only —
     the URL alone opens nothing. */
  function takeShow() {
    var v = ssJSON(K_SHOW);
    ssDel(K_SHOW);
    if (!v || !v.t || Date.now() - v.t > SHOW_TTL_MS) return null;
    if (v.s !== 'register' && v.s !== 'transfer' && v.s !== 'basket') return null;
    return v;
  }

  function showFromResumePage() {
    var show = takeShow();
    if (!show) return;
    S.busy = true;
    acquireResumeButton(function (btn) {
      S.busy = false;
      if (show.s === 'basket' || !btn || basketFlag(btn) !== 'true' || !C.loggedIn) return;
      btn._skqAgDrawer = inDrawer(btn);
      S.lines = parseLines(btn.getAttribute('data-skq-alumier-lines'));
      open(show.s, btn);
      if (show.e) showError('register', 'Something went wrong. Please try again.');
    });
  }

  /* Back/forward onto a page we resumed on: show it, never auto-checkout.
     An old /cart resume page is never meant to be seen — go to the origin. */
  function handleReturnVisit() {
    unhidePage();
    var st = history.state && typeof history.state === 'object' ? history.state : null;
    var path = window.location.pathname.replace(/\/+$/, '') || '/';
    var cartPath = (ds.cartUrl || '/cart').replace(/\/+$/, '');
    if (path === cartPath && st && st.skqAgResumed) {
      var dest = safePath(st.skqAgOrigin) || safePath(ssGet(K_LAST_ORIGIN)) || shopRoot;
      var destBare = dest.split('?')[0].replace(/\/+$/, '') || '/';
      if (destBare === cartPath) dest = shopRoot;
      window.location.replace(dest);
    }
  }

  (function boot() {
    /* layout/theme.liquid <head> is already leaving this page (for the resume page, or the
       origin): keep the cover and start nothing — above all, don't use up skq_ag_pending. */
    if (window.__skqAgHop) return;

    if (onResumePage) {
      /* Back/Forward visits were sent to the origin by the resume layout's <head> */
      if (!isBackForward()) resumeOnResumePage();
      return;
    }

    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { unhidePage(); return; }
    var gateParam = params.get(RESUME_PARAM);
    var resumeParam = gateParam === 'resume';

    if (isBackForward()) {
      /* Shown again via Back/Forward: strip a leftover param, show the page, no auto-checkout */
      if (gateParam) stripResumeParam(pendingOrigin());
      handleReturnVisit();
      return;
    }

    if (gateParam === 'show') {
      /* Sent here by the fast resume page: open the pop-up / basket it handed over */
      stripResumeParam('');
      unhidePage();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { setTimeout(showFromResumePage, 0); });
      } else {
        setTimeout(showFromResumePage, 0);
      }
      return;
    }

    if (resumeParam) {
      /* Clean the URL straight away; the resume itself starts at DOMContentLoaded
         (deferred scripts — drawer, consent modal, cart terms — are wired by then). */
      stripResumeParam(pendingOrigin());
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { setTimeout(resume, 0); });
      } else {
        setTimeout(resume, 0);
      }
      return;
    }
    /* The jump after login happens in layout/theme.liquid <head>, before first
       paint (hop pages returned above). Anything else: make sure the page shows. */
    unhidePage();
  })();

  /* Debugging only */
  window.SkAlumierCheckoutGate = {
    state: S,
    customer: { loggedIn: C.loggedIn, hasId: !!C.id },
    open: open,
    close: close
  };
})();
